import React, { useRef, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { showErrorMessage } from 'lowcomp/plugins/member/utils';
import { getLang } from 'lowcomp/plugins/member/utils';
import css from './index.module.scss';
import NextImage from '@/components/NextImage';
import { checkRewardDraw, rewardDraw } from '@/service';
import getLocal from '@/app/utils/getLocal';
import EditorImg from 'lowcode/components/EditorImg';
import { textLang } from 'lowcomp/plugins/member/common/textLang';
import { useRouter } from 'next/router';
import gtm from '@/utils/gtm';
import { signupCookie } from '@/utils/cookies/signupCookie';
import { createPortal } from 'react-dom';
import LoginFormModal from '@/components/LoginFormModal';

interface Prizes {
    drawId: React.Key; // 抽奖活动ID
    id: React.Key;
    isDisplayName: boolean;
    prizeId: React.Key; // 奖品ID
    prizeName: string;
    tips: string;
    type: 'NOTHING' | 'INTEGRAL' | 'COUPONS' | 'FREE_PRODUCTS' | 'THIRD_COUPONS';
    iconUrl?: string;
    isDisplayIcon?: boolean;
}

interface LuckyWheelProps {
    isLogin?: boolean;
    prizes: Prizes[];
    duration?: number;
    circle?: number;
    disabled?: boolean;
    drawInfo?: any;
    onStartRotating?: () => void;
    onEndRotating?: (winnerPrize?: any) => void;
    onCheckError?: () => void;
    pathUrl?: string;
    bgImage: string;
    btnImage: string;
    btnDisabledImage: string;
    available: number;
    limitFlag: boolean;
    loginType?: 'page' | 'modal';
    activeIndex: number | null;
    setActiveIndex:  React.Dispatch<React.SetStateAction<number | null>>;
}

const CIRCLE_ANGLE = 360;

const LuckyWheel: React.FC<LuckyWheelProps> = ({
    isLogin,
    prizes,
    duration = 10 * 1000,
    circle = 12,
    disabled = false,
    drawInfo,
    onStartRotating,
    onEndRotating,
    onCheckError,
    pathUrl,
    bgImage,
    btnImage,
    btnDisabledImage,
    loginType = 'page',
    available,
    limitFlag,
    activeIndex,
    setActiveIndex,
}) => {
    const router = useRouter();
    const { locale, pathname } = getLocal();
    const t = getLang(textLang, locale);
    const [isRotating, setIsRotaing] = useState(false);
    

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const activeIndexRef = useRef<number | null>(null);

    /**
     * @desc 将奖励给长度统一为8
     */
    const formatPrizeList = (prizes: Prizes[]) => {
        if (!Array.isArray(prizes)) return Array(8).fill(null);
        if (prizes?.length === 0) return Array(8).fill(null);
        if (prizes.length === 8) return prizes;
        return [...prizes, ...Array(8 - prizes.length).fill(null)];
    };

    const startSpinning = () => {
        if (isRotating) return;
        setIsRotaing(true);
        intervalRef.current = setInterval(() => {
            setActiveIndex((prev) => {
                const next = prev === null ? 0 : prev + 1;
                activeIndexRef.current = next;
                return next;
            });
        }, 150);
    };

    const stopAtWinner = (winningIndex: number, prize: any) => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        const MIN_STEPS = 8; // 至少转的步数（可调）
        let step = 0;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const runStep = () => {
            step++;
            const prev = activeIndexRef.current ?? 0;
            const next = prev + 1;
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }

            setActiveIndex(next);
            activeIndexRef.current = next; // 自己同步更新，不放在 updater 里

            const currentCell = next % 8;
            const reachedTarget = currentCell === winningIndex;
            const enoughSteps = step >= MIN_STEPS;

            if (reachedTarget && enoughSteps) {
                setIsRotaing(false);
                onEndRotating?.(prize);
            } else {
                timer = setTimeout(runStep, 300 + step * 20);
            }
        };

        runStep();
    };

    /**
     * @desc 转盘转动角度
     */
    const onRotating = async () => {
        if (disabled) return;
        if (isRotating) return;
        if (prizeList.length !== 8 || prizeList.some((item) => !item || !item.prizeId)) {
            message.error(t('network_error'));
            return;
        }
        if (!isLogin) {
            signupCookie({
                sector_name: 'lucky-whell',
                entry_position: 0,
            });
            if (loginType === 'modal') {
                setShowLoginModal(true);
                return;
            }
            router.push(`/${locale}/login?redirect=${window.location.href}`);
            return;
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_button',
            page_name: pathname,
            list_name: 'Lucky Spin',
            button_name: 'Go',
            function: 'Lucky Spin',
        });
        // check
        const { id: drawId, version } = drawInfo;
        try {
            const checkRes = await checkRewardDraw({ drawId, version }, locale, pathUrl);
            if (checkRes?.code !== '0') {
                setIsRotaing(false);
                showErrorMessage(checkRes?.code, t);
                onCheckError?.();
                return;
            }
        } catch (e) {
            message.error(t('network_error'));
            setIsRotaing(false);
            onCheckError?.();
            console.error('e', e);
            return;
        }

        startSpinning();
        // 先转两秒
        await new Promise((resolve) => setTimeout(resolve, 2000));
        try {
            const prizeRes = await rewardDraw({ drawId, version }, locale, pathUrl);
            if (prizeRes?.code === '0') {
                const { awardsId } = prizeRes?.data || {};
                let prizeIdx = prizeList.findIndex((item) => item?.id === awardsId);
                // 没有找到对应的奖品
                // 找到谢谢惠顾的奖品索引
                if (prizeIdx === -1) {
                    prizeIdx = prizeList.findIndex((item) => item?.type === 'NOTHING');
                }
                if (prizeIdx >= 0) {
                    stopAtWinner(prizeIdx, prizeRes?.data || prizeList[prizeIdx] || {});
                } else {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    setActiveIndex(null);
                    activeIndexRef.current = null;
                    message.error(t('network_error'));
                    setIsRotaing(false);
                    onCheckError?.();
                    return;
                }
            } else {
                const { awardsId } = prizeRes?.data || {};
                let idx = null;
                if (awardsId) {
                    idx = prizeList.findIndex((item) => item?.id === awardsId);
                }
                intervalRef.current && clearInterval(intervalRef.current);
                intervalRef.current = null;
                showErrorMessage(prizeRes?.code, t);
                setActiveIndex(idx);
                setIsRotaing(false);
                onCheckError?.();
                return;
            }
        } catch (e) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            activeIndexRef.current = null;
            setActiveIndex(null);
            message.error(t('network_error'));
            setIsRotaing(false);
        }
    };

    const prizeList = formatPrizeList(prizes);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);

    return (
        <div className={css['luckBg']}>
            <div className={`luck-whell-bg-main`}>
                {prizeList.map((prize, index) => (
                    <div
                        key={index}
                        className={`prize-item ${prize?.type === 'NOTHING' ? 'empty' : ''} ${activeIndex && activeIndex % 8 === index ? 'active' : ''}`}
                    >
                        {prize?.isDisplayIcon && prize?.iconUrl && (
                            <div className="prize-item__icon" style={{ backgroundImage: `url("${prize.iconUrl}")` }}></div>
                        )}
                        {prize?.isDisplayName && prize?.prizeName && <div className="prize-item__name">{prize?.prizeName}</div>}
                    </div>
                ))}

                <div className="grid_prize_point" onClick={onRotating} style={{ backgroundImage: `url("${!disabled ? btnImage : btnDisabledImage}")` }}>
                    <p className="grid_prize_point__title">GO</p>
                    {limitFlag && <p className="grid_prize_point__available">{available + ' Times'}</p>}
                </div>
            </div>

            {isClient &&
                showLoginModal &&
                createPortal(
                    <LoginFormModal
                        visible={showLoginModal}
                        onCancel={() => {
                            setShowLoginModal(false);
                        }}
                        type="passwordless"
                    />,
                    document.body,
                )}
        </div>
    );
};

export default LuckyWheel;
